import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Mail, Phone, Building, Download, Upload, AlertCircle, CheckCircle2, User, Trash2, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCustomers } from '../hooks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CustomerList = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nameExists, setNameExists] = useState(false);
  const [existingName, setExistingName] = useState('');
  const [checkingName, setCheckingName] = useState(false);
  const fileInputRef = useRef(null);
  const nameCheckTimeoutRef = useRef(null);
  const { user } = useAuth();
  
  // Use custom hook for customers data
  const { customers, loading, refetch: refetchCustomers } = useCustomers();
  
  // Duplicate cleanup state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [deletingDuplicates, setDeletingDuplicates] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: ''
  });

  // Check if customer name already exists (debounced)
  const checkNameExists = useCallback(async (name) => {
    if (!name || name.trim().length < 2) {
      setNameExists(false);
      setExistingName('');
      return;
    }
    
    setCheckingName(true);
    try {
      const response = await axios.get(`${API}/customers/check-name`, {
        params: { name: name.trim() }
      });
      setNameExists(response.data.exists);
      setExistingName(response.data.customer_name || '');
    } catch (error) {
      console.error('Failed to check name:', error);
    } finally {
      setCheckingName(false);
    }
  }, []);

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setFormData({ ...formData, name: newName });
    
    // Clear previous timeout
    if (nameCheckTimeoutRef.current) {
      clearTimeout(nameCheckTimeoutRef.current);
    }
    
    // Debounce the name check (300ms delay)
    nameCheckTimeoutRef.current = setTimeout(() => {
      checkNameExists(newName);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission if name already exists
    if (nameExists) {
      toast.error(`Customer "${existingName}" already exists`);
      return;
    }
    
    if (submitting) return; // Prevent double submission
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/customers`, formData);
      toast.success('Customer created successfully');
      setDialogOpen(false);
      setFormData({ name: '', email: '', phone: '', company: '', notes: '' });
      setNameExists(false);
      setExistingName('');
      refetchCustomers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create customer';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset name validation when dialog closes
  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      setNameExists(false);
      setExistingName('');
      setFormData({ name: '', email: '', phone: '', company: '', notes: '' });
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/customers/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customers_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Customers exported successfully');
    } catch (error) {
      toast.error('Failed to export customers');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    const importFormData = new FormData();
    importFormData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/customers/import`, importFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setImportDialogOpen(false);
      refetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import customers');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Duplicate cleanup functions
  const fetchDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const response = await axios.get(`${API}/admin/customer-duplicates`);
      setDuplicateData(response.data);
    } catch (error) {
      toast.error('Failed to check for duplicates');
      setDuplicateDialogOpen(false);
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleDeleteDuplicates = async () => {
    if (!duplicateData || duplicateData.total_duplicates === 0) return;
    
    setDeletingDuplicates(true);
    try {
      const response = await axios.delete(`${API}/admin/customer-duplicates`);
      toast.success(response.data.message);
      setDuplicateDialogOpen(false);
      setDuplicateData(null);
      refetchCustomers();
    } catch (error) {
      toast.error('Failed to delete duplicates');
    } finally {
      setDeletingDuplicates(false);
    }
  };

  const handleDuplicateDialogOpen = (open) => {
    setDuplicateDialogOpen(open);
    if (open) {
      fetchDuplicates();
    } else {
      setDuplicateData(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <Navbar setSidebarOpen={setSidebarOpen} />
      
      <main className="md:ml-64 pt-16">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="customers-title">Customers</h1>
              <p className="text-slate-600">Manage your customer database</p>
            </div>
            <div className="flex items-center gap-2">
              {user?.role === 'admin' && (
                <Dialog open={duplicateDialogOpen} onOpenChange={handleDuplicateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="cleanup-duplicates-button">
                      <Search className="h-4 w-4 mr-2" />
                      Find Duplicates
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="h-5 w-5 text-red-600" />
                        Duplicate Customer Cleanup
                      </DialogTitle>
                    </DialogHeader>
                    
                    {loadingDuplicates ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                        <span className="ml-3 text-slate-600">Scanning for duplicates...</span>
                      </div>
                    ) : duplicateData ? (
                      <div className="space-y-4">
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-sm text-slate-600">Total Customers: <span className="font-bold">{duplicateData.total_customers}</span></p>
                          <p className="text-sm text-slate-600">Duplicate Records Found: <span className="font-bold text-red-600">{duplicateData.total_duplicates}</span></p>
                        </div>
                        
                        {duplicateData.total_duplicates === 0 ? (
                          <div className="text-center py-8">
                            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                            <p className="text-lg font-medium text-green-700">No duplicates found!</p>
                            <p className="text-sm text-slate-500">Your customer database is clean.</p>
                          </div>
                        ) : (
                          <>
                            <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                              {duplicateData.duplicate_groups.map((group, idx) => (
                                <div key={idx} className="p-3">
                                  <p className="text-xs font-medium text-slate-500 uppercase mb-2">
                                    {group.type === 'phone' ? 'Phone' : 'Email'}: {group.value}
                                  </p>
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                                      <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-green-800 truncate">KEEP: {group.keep.name}</p>
                                        <p className="text-xs text-green-600">{group.keep.phone} - {group.keep.email}</p>
                                      </div>
                                    </div>
                                    {group.delete.map((dup, dupIdx) => (
                                      <div key={dupIdx} className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200">
                                        <Trash2 className="h-4 w-4 text-red-600 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-red-800 truncate">DELETE: {dup.name}</p>
                                          <p className="text-xs text-red-600">{dup.phone} - {dup.email}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <p className="text-sm text-red-800 font-medium mb-2">Warning</p>
                              <p className="text-xs text-red-700">
                                This will permanently delete {duplicateData.total_duplicates} duplicate customer records 
                                and their associated enquiries, tickets, and marketing leads. The oldest record (first created) will be kept.
                              </p>
                            </div>
                            
                            <Button 
                              onClick={handleDeleteDuplicates} 
                              disabled={deletingDuplicates}
                              className="w-full bg-red-600 hover:bg-red-700"
                              data-testid="confirm-delete-duplicates-button"
                            >
                              {deletingDuplicates ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete {duplicateData.total_duplicates} Duplicates
                                </>
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    ) : null}
                  </DialogContent>
                </Dialog>
              )}
              <Button variant="outline" onClick={handleExport} data-testid="export-customers-button">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="import-customers-button">
                    <Upload className="h-4 w-4 mr-2" />
                    Import
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Import Customers from Excel</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Upload an Excel file (.xlsx) with columns: Name, Email, Phone, Company, Notes
                    </p>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleImport}
                      disabled={importing}
                      data-testid="import-file-input"
                    />
                    {importing && <p className="text-sm text-slate-600">Importing...</p>}
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
                <DialogTrigger asChild>
                  <Button data-testid="create-customer-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Customer
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Customer</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <div className="relative">
                        <Input 
                          id="name" 
                          value={formData.name} 
                          onChange={handleNameChange} 
                          required 
                          data-testid="customer-name-input"
                          className={nameExists ? 'border-red-500 pr-10' : formData.name.length >= 2 && !nameExists && !checkingName ? 'border-green-500 pr-10' : ''}
                        />
                        {checkingName && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-400"></div>
                          </div>
                        )}
                        {!checkingName && nameExists && (
                          <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-red-500" />
                        )}
                        {!checkingName && formData.name.length >= 2 && !nameExists && (
                          <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
                        )}
                      </div>
                      {nameExists && (
                        <p className="text-sm text-red-500 flex items-center gap-1" data-testid="name-exists-error">
                          <AlertCircle className="h-3 w-3" />
                          Customer &quot;{existingName}&quot; already exists
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="customer-email-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="customer-phone-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input id="company" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} data-testid="customer-company-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} data-testid="customer-notes-input" />
                    </div>
                    <Button type="submit" className="w-full" disabled={nameExists || checkingName || submitting} data-testid="submit-customer-button">
                      {submitting ? 'Creating...' : checkingName ? 'Checking...' : 'Create Customer'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Customers Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <p className="text-slate-600">No customers yet. Create your first customer to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {customers.map((customer) => (
                <Link key={customer.id} to={`/customers/${customer.id}`} className="bg-white rounded-xl border border-slate-200 p-6 card-hover" data-testid={`customer-card-${customer.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 rounded-full bg-accent text-white flex items-center justify-center font-semibold text-lg">
                      {customer.name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">{customer.name}</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600"><Mail className="h-4 w-4" /><span className="truncate">{customer.email}</span></div>
                    <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="h-4 w-4" /><span>{customer.phone}</span></div>
                    {customer.company && <div className="flex items-center gap-2 text-sm text-slate-600"><Building className="h-4 w-4" /><span className="truncate">{customer.company}</span></div>}
                    {customer.created_by_name && <div className="flex items-center gap-2 text-sm text-slate-500"><User className="h-4 w-4" /><span>Created by {customer.created_by_name}</span></div>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
